import React, { useState, useEffect, useContext } from "react";
import {
  FaUsers,
  FaBell,
  FaSignOutAlt,
  FaUser,
  FaUserCircle,
  FaEnvelope,
  FaPhone,
  FaBirthdayCake,
  FaCalendarAlt,
  FaUserTie,
  FaMapMarkerAlt
} from "react-icons/fa";
import { PiSpeedometerBold } from "react-icons/pi";
import { BsMenuButtonWide } from "react-icons/bs";
import { MdOutlineViewComfy, MdBusiness, MdOutlineLocationOn } from "react-icons/md";
import { TbReportSearch, TbActivityHeartbeat } from "react-icons/tb";
import { SlMenu } from "react-icons/sl";
import { Link, useLocation, useNavigate } from "react-router-dom";
import api from "../../services/AxiosConnection";
import AuthContext from "../../context/AuthContext";
import useAuth from "../../hooks/useAuth";
import { useNotification } from "../../context/NotificationContext";
import { TbMessageReportFilled } from "react-icons/tb";

function Navbar() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [sites, setSites] = useState([]);
  const [isUserPopupOpen, setIsUserPopupOpen] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [userImage, setUserImage] = useState(null);
  const [isMobileView, setIsMobileView] = useState(false);
  const { triggerNotification } = useNotification();
  const {
    selectedSite,
    setSelectedSite,
    selectedSiteName,
    setSelectedSiteName,
    fromDate,
    toDate,
  } = useContext(AuthContext);
  const location = useLocation();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));
  const userRole = user?.role || "";
  const userName = user?.username || "Guest";

  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 768;
      const isTabletPortrait =
        window.innerWidth >= 768 &&
        window.innerWidth <= 1024 &&
        window.innerHeight > window.innerWidth;
      setIsMobileView(isMobile || isTabletPortrait);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleReportIssue = () => {
    triggerNotification("Report issue feature is under development", "info");
  };

  const handleNotificationClick = () => {
    triggerNotification("Notification feature is under development", "info");
  };

  const fetchUserImage = async (filePath) => {
    try {
      const response = await api.get("/users/get-file", {
        params: { filePath },
        responseType: "blob",
      });
      return URL.createObjectURL(response.data);
    } catch (error) {
      console.error("Error fetching user image:", error);
      triggerNotification("Failed to fetch user image", "error");
      return null;
    }
  };

  useEffect(() => {
    if (!user?.id || userDetails) return;
    const fetchUserData = async () => {
      try {
        const response = await api.get(`/users/get-user?userId=${user.id}`);
        const userData = response.data.user;
        setUserDetails(userData);
        if (userData.imageFilePath) {
          const imageUrl = await fetchUserImage(userData.imageFilePath);
          setUserImage(imageUrl);
        }
      } catch (error) {
        console.error("Error fetching user details:", error);
        triggerNotification("Failed to fetch user details", "error");
      }
    };
    fetchUserData();
    return () => {
      if (userImage) {
        URL.revokeObjectURL(userImage);
      }
    };
  }, [user?.id]);

  const handleUserClick = () => {
    if (!user?.id) {
      triggerNotification("No user ID found", "error");
      return;
    }
    setIsUserPopupOpen(true);
  };

  useEffect(() => {
    if (user && user.id) {
      api
        .get(`/users/get-sites`, { params: { userId: user.id } })
        .then((response) => {
          if (response.data && response.data.sites) {
            const fetchedSites = response.data.sites;
            setSites(fetchedSites);
            const savedSite = localStorage.getItem("selectedSite");
            if (!savedSite && fetchedSites.length > 0) {
              const initialSite = fetchedSites[0];
              setSelectedSite(initialSite.siteDbName);
              setSelectedSiteName(initialSite.siteName);
              localStorage.setItem("selectedSite", initialSite.siteDbName);
              localStorage.setItem("selectedSiteName", initialSite.siteName);
            }
          }
        })
        .catch((error) => {
          console.error("Error fetching sites:", error);
        });
    }
  }, [user?.id, setSelectedSite, setSelectedSiteName]);

  useEffect(() => {
    if (isUserPopupOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isUserPopupOpen]);

  const handleSiteChange = (e) => {
    const selectedDbName = e.target.value;
    const matchedSite = sites.find((s) => s.siteDbName === selectedDbName);
    setSelectedSite(selectedDbName);
    setSelectedSiteName(matchedSite?.siteName || "");
    localStorage.setItem("selectedSite", selectedDbName);
    localStorage.setItem("selectedSiteName", matchedSite?.siteName || "");
  };

  const handleLogout = async () => {
    try {
      // Call the logout API
      await api.put("/users/logout");
      
      // Clear local storage and navigate to login
      logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout API error:", error);
      // Even if the API call fails, we should still log the user out locally
      logout();
      navigate("/login");
    }
  };

  const fullNavItems = [
    {
      title: "CONTROL PANEL",
      icon: <PiSpeedometerBold />,
      to: "/dashboard/control-panel",
    },
    {
      title: "DIVISION VIEW",
      icon: <MdBusiness />,
      to: "/dashboard/division-details",
    },
    {
      title: "SITE OVERVIEW",
      icon: <MdOutlineLocationOn />,
      to: "/dashboard/site-overview",
    },
    {
      title: "SIMPLE VIEW",
      icon: <BsMenuButtonWide />,
      to: "/dashboard/simple-view",
    },
    {
      title: "DETAIL VIEW",
      icon: <MdOutlineViewComfy />,
      to: "/dashboard/detail-view",
    },
    {
      title: "REPORTS",
      icon: <TbReportSearch />,
      to: "/dashboard/reports",
    },
    {
      title: "DIAGNOSTIC",
      icon: <TbActivityHeartbeat />,
      to: "/dashboard/diagnostic",
    },
    {
      title: "USER MGMT",
      icon: <FaUsers />,
      to: "/dashboard/user-management",
      roles: ["Admin", "Super Admin"],
    },
  ];

  const navItems = fullNavItems.filter(
    (item) => !item.roles || item.roles.includes(userRole)
  );

  return (
    <div className="fixed top-24 sm:top-16 left-0 right-0 z-40 bg-blue-900 max-w-full shadow-sm">
      <nav className="bg-blue-900">
        {/* Mobile and Tablet Portrait View */}
        {isMobileView ? (
          <div className="flex justify-between items-center px-4 py-1 h-10"> 
            <SlMenu
              className="text-white text-[20px] cursor-pointer"
              onClick={() => setIsSidebarOpen(true)}
            />
            <div className="flex justify-end px-4 py-1 space-x-2.5">
              <div className="relative py-0.5">
                <select
                  className="bg-white px-2.5 py-1 text-[12px] rounded-md h-8" 
                  value={selectedSite}
                  onClick={(e) => e.stopPropagation()}
                  onChange={handleSiteChange}
                >
                  <option disabled value="">
                    Select Site
                  </option>
                  {sites.map((site, index) => (
                    <option key={index} value={site.siteDbName}>
                      {site.siteName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="relative py-0.5 flex items-center space-x-2.5">
                <TbMessageReportFilled
                  className="text-white text-[18px] cursor-pointer z-10" 
                  onClick={handleReportIssue}
                  title="Report Issue"
                />
                <FaBell
                  className="text-white text-[18px] cursor-pointer z-10" 
                  onClick={handleNotificationClick}
                  title="Notifications"
                />
              </div>
              <div
                className="flex flex-col items-center space-x-0 space-y-0 cursor-pointer mx-1"
                onClick={handleUserClick}
              >
                {userImage ? (
                  <img
                    src={userImage}
                    alt={`${userName}'s profile`}
                    className="w-5 h-5 rounded-full object-cover" 
                  />
                ) : (
                  <FaUser className="text-white text-[18px]" /> 
                )}
                <span className="text-white text-[10px] uppercase">{userName}</span> 
              </div>
            </div>
          </div>
        ) : (
          /* Desktop and Tablet Landscape View */
          <div className="flex justify-between items-center overflow-x-auto flex-nowrap">
            <div className="flex flex-nowrap">
              {navItems.map((item, index) => (
                <Link
                  to={item.to}
                  key={index}
                  className="text-white flex-shrink-0 hover:bg-blue-800"
                >
                  <div
                    className={`flex flex-col items-center px-2 py-0.5 h-full border-r border-gray-300 w-22 md:w-26 lg:w-30 ${location.pathname === item.to ? "bg-red-500" : ""
                      }`}
                  >
                    <div className="text-[14px] mb-0.5">{item.icon}</div>
                    <span className="text-[11px] px-1.5 text-center whitespace-normal font-medium uppercase tracking-tight">
                      {item.title}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
            {/* Right Actions */}
            <div className="ml-auto flex items-center px-2 flex-shrink-0 space-x-2.5">
              <div className="relative mx-1.5">
                <select
                  className="bg-white px-2 py-1 text-[11px] rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  value={selectedSite}
                  onClick={(e) => e.stopPropagation()}
                  onChange={handleSiteChange}
                >
                  <option disabled value="">
                    Select Site
                  </option>
                  {sites.map((site, index) => (
                    <option key={index} value={site.siteDbName}>
                      {site.siteName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="relative mx-1.5 flex items-center space-x-2.5">
                <TbMessageReportFilled
                  className="text-[18px] text-white cursor-pointer hover:text-blue-200 transition-colors"
                  onClick={handleReportIssue}
                  title="Report Issue"
                />
                <FaBell
                  className="text-[14px] text-white cursor-pointer hover:text-blue-200 transition-colors"
                  onClick={handleNotificationClick}
                  title="Notifications"
                />
              </div>
              <div
                className="flex flex-col items-center cursor-pointer mx-1.5"
                onClick={handleUserClick}
              >
                {userImage ? (
                  <img
                    src={userImage}
                    alt={`${userName}'s profile`}
                    className="w-5 h-5 md:w-5.5 md:h-5.5 lg:w-6 lg:h-6 rounded-full object-cover mt-0.5"
                  />
                ) : (
                  <FaUser className="text-[14px] text-white mt-0.5" />
                )}
                <span className="text-white text-[11px] uppercase">{userName}</span>
              </div>
              <div
                className="flex flex-col items-center cursor-pointer mx-1.5"
                onClick={handleLogout}
              >
                <div className="text-red-500 text-[14px] mb-0.5">
                  <FaSignOutAlt />
                </div>
                <span className="text-white text-[11px] px-1.5 text-center whitespace-nowrap font-medium uppercase tracking-tight">
                  LOG OUT
                </span>
              </div>
            </div>
          </div>
        )}
      </nav>
      {/* Sidebar for mobile and tablet portrait */}
      <div
        className={`fixed inset-0 z-50 bg-black bg-opacity-50 ${isMobileView ? "block" : "hidden"} ${isSidebarOpen ? "block" : "hidden"
          }`}
        onClick={() => setIsSidebarOpen(false)}
      >
        <div
          className={`w-52 bg-blue-900 text-white h-full p-4 overflow-y-auto transform transition-all duration-300 ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"
            }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-center mb-3">
            <span className="text-lg font-semibold">Menu</span>
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="text-[18px]"
            >
              ×
            </button>
          </div>
          {navItems.map((item, index) => (
            <Link to={item.to} key={index} className="text-white">
              <div
                className={`flex items-center mb-3 cursor-pointer text-[12px] ${location.pathname === item.to
                    ? "bg-red-500"
                    : "hover:bg-blue-800"
                  } px-2.5 py-1.5 rounded-md`}
              >
                <div className="text-[20px]">{item.icon}</div>
                <span className="ml-1.5 px-1.5">{item.title}</span>
              </div>
            </Link>
          ))}
          <div
            className="flex items-center mb-3 cursor-pointer px-2.5 py-1.5 rounded-md hover:bg-blue-800"
            onClick={handleLogout}
          >
            <div className="text-[20px] text-red-500">
              <FaSignOutAlt />
            </div>
            <span className="ml-1.5 px-1.5 text-[12px] text-white">LOG OUT</span>
          </div>
        </div>
      </div>
      {/* User Details Popup */}
      {isUserPopupOpen && userDetails && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-3"
          onClick={() => setIsUserPopupOpen(false)}
        >
          <div
            className="bg-white rounded-md p-4 max-w-md w-full relative shadow-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setIsUserPopupOpen(false)}
              className="absolute top-3 right-3 text-[18px] text-gray-600 hover:text-gray-900"
            >
              ×
            </button>
            <div className="flex flex-col items-center space-y-3">
              {userImage ? (
                <img
                  src={userImage}
                  alt={`${userDetails.username}'s profile`}
                  className="w-20 h-20 rounded-full object-cover border border-gray-200"
                />
              ) : (
                <FaUserCircle className="w-20 h-20 text-gray-400" />
              )}
              <div className="space-y-1.5 text-[12px]">
                <p className="flex items-center">
                  <FaUserTie className="mr-1.5 text-[14px]" />
                  <strong className="mr-1.5">Username:</strong>
                  {userDetails.username || "N/A"}
                </p>
                <p className="flex items-center">
                  <FaEnvelope className="mr-1.5 text-[14px]" />
                  <strong className="mr-1.5">Email:</strong>
                  {userDetails.email || "N/A"}
                </p>
                <p className="flex items-center">
                  <FaPhone className="mr-1.5 text-[14px]" />
                  <strong className="mr-1.5">Primary Mobile No:</strong>
                  {userDetails.primary_mobile_no || "N/A"}
                </p>
                <p className="flex items-center">
                  <FaPhone className="mr-1.5 text-[14px]" />
                  <strong className="mr-1.5">Secondary Mobile No:</strong>
                  {userDetails.secondary_mobile_no || "N/A"}
                </p>
                <p className="flex items-center">
                  <FaBirthdayCake className="mr-1.5 text-[14px]" />
                  <strong className="mr-1.5">Date of Birth:</strong>
                  {userDetails.date_of_birth
                    ? new Date(userDetails.date_of_birth).toLocaleDateString()
                    : "N/A"}
                </p>
                <p className="flex items-center">
                  <FaCalendarAlt className="mr-1.5 text-[14px]" />
                  <strong className="mr-1.5">Date of Joining:</strong>
                  {userDetails.date_of_joining
                    ? new Date(userDetails.date_of_joining).toLocaleDateString()
                    : "N/A"}
                </p>
                <p className="flex items-center">
                  <FaUserTie className="mr-1.5 text-[14px]" />
                  <strong className="mr-1.5">Reporting Manager:</strong>
                  {userDetails.reporting_manager || "N/A"}
                </p>
                <p className="flex items-center">
                  <FaMapMarkerAlt className="mr-1.5 text-[14px]" />
                  <strong className="mr-1.5">Division Name:</strong>
                  {userDetails.division_name || "N/A"}
                </p>
                <p className="flex items-center flex-wrap">
                  <FaMapMarkerAlt className="mr-1.5 text-[14px]" />
                  <strong className="mr-1.5">Site Names:</strong>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {userDetails.site_names && typeof userDetails.site_names === "string" && userDetails.site_names.trim() ? (
                      userDetails.site_names.split(",").map((site, index) => (
                        <span
                          key={index}
                          className="bg-gray-200 text-gray-700 px-2 py-1 rounded-full text-[11px]"
                        >
                          {site.trim()}
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-500 text-[11px]">No sites assigned</span>
                    )}
                  </div>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Navbar;